// Copyright (c) Wiens Consulting. All rights reserved.

import { useState, useEffect, useCallback } from "react"
import { Link } from "react-router-dom"
import {
  IconFileDescription,
  IconChevronRight,
  IconCalendar,
  IconCash,
} from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Card, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { api } from "@/lib/api"

interface BenefitProgramSummary {
  id: number
  name: string
  description: string
  availableProgramYears: number[]
  fundingSourceCount: number
}

export default function BenefitProgramsPage() {
  const [programs, setPrograms] = useState<BenefitProgramSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadPrograms = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await api.get<BenefitProgramSummary[]>("/api/benefit-programs")
      setPrograms(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load benefit programs")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadPrograms()
  }, [loadPrograms])

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <div className="flex items-center gap-2">
        <IconFileDescription className="size-6" />
        <h1 className="text-2xl font-bold">Benefit Programs</h1>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-40" />
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : programs.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No benefit programs found.
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {programs.map((program) => (
            <Link key={program.id} to={`/benefit-programs/${program.id}`}>
              <Card className="transition-colors hover:bg-muted/50 cursor-pointer h-full">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="min-w-0">
                        <CardTitle className="text-lg">{program.name}</CardTitle>
                        <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{program.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <Badge variant="secondary" className="text-xs">
                        <IconCalendar className="size-3 mr-1" />
                        {program.availableProgramYears.length}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        <IconCash className="size-3 mr-1" />
                        {program.fundingSourceCount}
                      </Badge>
                      <IconChevronRight className="size-4 text-muted-foreground" />
                    </div>
                  </div>
                </CardHeader>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
